import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types';
import { getObjectId } from '@/helpers/object.id.helper';

export type InBetweenPlayoutSegmentKind = 'flow_transition' | 'flow_resume';

export function isInBetweenPlayoutSegmentKind(kind: string | null | undefined): kind is InBetweenPlayoutSegmentKind {
  return kind === 'flow_transition' || kind === 'flow_resume';
}

function isPlayableSlotSegmentKind(kind: string | null | undefined): kind is 'session' | 'break' {
  return kind === 'session' || kind === 'break';
}

/** Session → following transition; break → following resume. */
function preferredInBetweenKindForSlotSegment(activeSegmentKind: 'session' | 'break'): InBetweenPlayoutSegmentKind {
  return activeSegmentKind === 'session' ? 'flow_transition' : 'flow_resume';
}

function slotIdsEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) {
    return false;
  }
  return (getObjectId(a) ?? a) === (getObjectId(b) ?? b);
}

export type VirtualInBetweenPlayout = {
  segmentKind: InBetweenPlayoutSegmentKind;
  slotId: string;
  insertAfterSlotId: string;
  segmentId: string;
  isActive: boolean;
};

export function buildInBetweenPlayoutSegmentId(segmentKind: InBetweenPlayoutSegmentKind, slotId: string): string {
  return `__flow_${segmentKind}_playout__${slotId}__`;
}

export function parseInBetweenPlayoutSegmentId(activeSlotId: string): {
  segmentKind: InBetweenPlayoutSegmentKind;
  slotId: string;
} | null {
  const match = /^__flow_(flow_transition|flow_resume)_playout__(.+)__$/.exec(activeSlotId);
  if (!match) {
    return null;
  }
  return {
    segmentKind: match[1] as InBetweenPlayoutSegmentKind,
    slotId: match[2]!,
  };
}

function slotIdFromSegmentAnchor(anchor: string | undefined): string | undefined {
  if (!anchor?.startsWith('slot:')) {
    return undefined;
  }
  return anchor.slice('slot:'.length);
}

function resolveInBetweenSlotIdFromRow(row: SimulationCueSheetRow): string | undefined {
  return row.slotId ?? slotIdFromSegmentAnchor(row.segmentAnchor);
}

export function resolveVirtualInBetweenPlayoutFromPointerRow(
  row: SimulationCueSheetRow | undefined,
  isActive = false,
): VirtualInBetweenPlayout | null {
  if (!row || !isInBetweenPlayoutSegmentKind(row.segmentKind)) {
    return null;
  }
  const slotId = resolveInBetweenSlotIdFromRow(row);
  if (!slotId) {
    return null;
  }
  return {
    segmentKind: row.segmentKind,
    slotId,
    insertAfterSlotId: slotId,
    segmentId: buildInBetweenPlayoutSegmentId(row.segmentKind, slotId),
    isActive,
  };
}

function resolveInBetweenKindFromCueState(cueState: FlowCueStateV1): InBetweenPlayoutSegmentKind | null {
  if (isInBetweenPlayoutSegmentKind(cueState.activeSegmentKind)) {
    return cueState.activeSegmentKind;
  }
  const stepScope = cueState.currentStep?.scope;
  if (isInBetweenPlayoutSegmentKind(stepScope)) {
    return stepScope;
  }
  return null;
}

function resolveInBetweenSlotIdFromCueState(
  cueState: FlowCueStateV1,
  segmentKind: InBetweenPlayoutSegmentKind,
): string | undefined {
  const stepScope = cueState.currentStep?.scope;
  if (stepScope === segmentKind) {
    return slotIdFromSegmentAnchor(cueState.currentStep?.segmentAnchor) ?? cueState.activeSlotId;
  }
  if (cueState.activeSegmentKind === segmentKind) {
    return cueState.activeSlotId ?? slotIdFromSegmentAnchor(cueState.currentStep?.segmentAnchor);
  }
  return cueState.activeSlotId;
}

function isCurrentStepInBetween(
  cueState: FlowCueStateV1,
  segmentKind: InBetweenPlayoutSegmentKind,
  slotId: string,
): boolean {
  const step = cueState.currentStep;
  if (!step || step.scope !== segmentKind) {
    return false;
  }
  const stepSlotId = slotIdFromSegmentAnchor(step.segmentAnchor) ?? cueState.activeSlotId;
  return stepSlotId === slotId;
}

/** True when in-between playout is the active cue step with live media (even if activeSegmentKind is still the prior slot). */
export function isInBetweenPlayoutPlayingInCueState(
  cueState: FlowCueStateV1,
  segmentKind: InBetweenPlayoutSegmentKind,
  slotId: string,
): boolean {
  if (isInBetweenPlayoutActiveInCueState(cueState, segmentKind, slotId)) {
    return true;
  }
  if (!isCurrentStepInBetween(cueState, segmentKind, slotId)) {
    return false;
  }
  return Object.values(cueState.items ?? {}).some(
    (item) =>
      item.isCurrent &&
      (item.phase === 'on_air' ||
        item.phase === 'preloading' ||
        item.phase === 'preloaded' ||
        item.phase === 'loading_with_fallback'),
  );
}

export function resolveInBetweenPlayoutFromCueState(
  cueState: FlowCueStateV1 | undefined,
): { segmentKind: InBetweenPlayoutSegmentKind; slotId: string } | null {
  if (!cueState) {
    return null;
  }
  const segmentKind = resolveInBetweenKindFromCueState(cueState);
  if (!segmentKind) {
    return null;
  }
  const slotId = resolveInBetweenSlotIdFromCueState(cueState, segmentKind);
  if (!slotId) {
    return null;
  }
  return { segmentKind, slotId };
}

function isInBetweenPlayoutActiveInCueState(
  cueState: FlowCueStateV1,
  segmentKind: InBetweenPlayoutSegmentKind,
  slotId: string,
): boolean {
  if (cueState.activeSegmentKind !== segmentKind || cueState.activeSlotId !== slotId) {
    return false;
  }
  return (
    cueState.isSegmentRunning === true ||
    Object.values(cueState.items ?? {}).some((item) => item.phase === 'on_air' && item.isCurrent)
  );
}

export function resolveVirtualInBetweenPlayoutFromCueState(
  cueState: FlowCueStateV1 | undefined,
): VirtualInBetweenPlayout | null {
  if (!cueState) {
    return null;
  }
  const segmentKind = resolveInBetweenKindFromCueState(cueState);
  if (!segmentKind) {
    return null;
  }
  const slotId = resolveInBetweenSlotIdFromCueState(cueState, segmentKind);
  if (!slotId) {
    return null;
  }
  return {
    segmentKind,
    slotId,
    insertAfterSlotId: slotId,
    segmentId: buildInBetweenPlayoutSegmentId(segmentKind, slotId),
    isActive: isInBetweenPlayoutPlayingInCueState(cueState, segmentKind, slotId),
  };
}

/**
 * Find the in-between playout row for a slot in the cue sheet (transition after session,
 * resume after break). Mirrors BE `FlowCueSegmentIndex.inBetweenForSlot`.
 */
export function resolveInBetweenPlayoutForSlotFromRows(
  rows: SimulationCueSheetRow[],
  slotId: string,
  preferredKind?: InBetweenPlayoutSegmentKind,
): { segmentKind: InBetweenPlayoutSegmentKind; slotId: string } | null {
  let preferred: { segmentKind: InBetweenPlayoutSegmentKind; slotId: string } | null = null;
  let fallback: { segmentKind: InBetweenPlayoutSegmentKind; slotId: string } | null = null;

  for (const row of rows) {
    if (!isInBetweenPlayoutSegmentKind(row.segmentKind)) {
      continue;
    }
    const rowSlotId = resolveInBetweenSlotIdFromRow(row);
    if (!rowSlotId || !slotIdsEqual(rowSlotId, slotId)) {
      continue;
    }
    const match = { segmentKind: row.segmentKind, slotId: rowSlotId };
    if (preferredKind && row.segmentKind === preferredKind) {
      preferred = match;
      break;
    }
    if (!fallback) {
      fallback = match;
    }
    if (!preferredKind) {
      // Same preference as BE: transition before resume when kind is unspecified.
      if (row.segmentKind === 'flow_transition') {
        preferred = match;
        break;
      }
    }
  }

  return preferred ?? fallback;
}

/**
 * While a session/break is the active playing segment, expose its following transition/resume
 * row in the Control list — not only when the cue pointer has reached that in-between segment.
 */
export function resolveFollowingInBetweenPlayoutForActiveSlot(args: {
  rows: SimulationCueSheetRow[];
  activeSegmentKind?: string | null;
  activeSlotId?: string | null;
  /** When already on the in-between step, mark the virtual row active. */
  isActive?: boolean;
}): VirtualInBetweenPlayout | null {
  const { rows, activeSegmentKind, activeSlotId, isActive = false } = args;
  if (!isPlayableSlotSegmentKind(activeSegmentKind) || !activeSlotId || rows.length === 0) {
    return null;
  }

  const preferredKind = preferredInBetweenKindForSlotSegment(activeSegmentKind);
  const match = resolveInBetweenPlayoutForSlotFromRows(rows, activeSlotId, preferredKind);
  if (!match) {
    return null;
  }

  return {
    segmentKind: match.segmentKind,
    slotId: match.slotId,
    insertAfterSlotId: match.slotId,
    segmentId: buildInBetweenPlayoutSegmentId(match.segmentKind, match.slotId),
    isActive,
  };
}

/**
 * Control list virtual row: prefer the in-between currently on air / under the pointer;
 * otherwise the following transition/resume for the active session/break segment.
 */
export function resolveVirtualInBetweenPlayout(args: {
  rows: SimulationCueSheetRow[];
  cueState?: FlowCueStateV1;
  /** Simulation: pointer row when not using cueState. */
  pointerRow?: SimulationCueSheetRow;
  activeSegmentKind?: string | null;
  activeSlotId?: string | null;
  isInBetweenPointerActive?: boolean;
}): VirtualInBetweenPlayout | null {
  const { rows, cueState, pointerRow, activeSegmentKind, activeSlotId, isInBetweenPointerActive = false } = args;

  if (cueState) {
    const fromCue = resolveVirtualInBetweenPlayoutFromCueState(cueState);
    if (fromCue) {
      return fromCue;
    }
    return resolveFollowingInBetweenPlayoutForActiveSlot({
      rows,
      activeSegmentKind: cueState.activeSegmentKind ?? activeSegmentKind,
      activeSlotId: cueState.activeSlotId ?? activeSlotId,
      isActive: false,
    });
  }

  const fromPointer = resolveVirtualInBetweenPlayoutFromPointerRow(pointerRow, isInBetweenPointerActive);
  if (fromPointer) {
    return fromPointer;
  }

  return resolveFollowingInBetweenPlayoutForActiveSlot({
    rows,
    activeSegmentKind,
    activeSlotId,
    isActive: false,
  });
}
